import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { QuillModule } from 'ngx-quill';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { TextContent } from '../../../models/text-content.model';
import { TextContentService } from '../../../services/text-content.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';

@Component({
  selector: 'app-text-content-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    ToolbarModule,
    ToastModule,
    RouterLink,
    QuillModule,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './text-content-update.component.html',
  styles: `
    :host ::ng-deep .content-editor .ql-editor {
      min-height: 320px;
      max-height: 600px;
      overflow-y: auto;
      width: 100%;
    }
  `,
})
export class TextContentUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private textContentService = inject(TextContentService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    slug: [{ value: null, disabled: true }, [Validators.required]],
    description: [{ value: null, disabled: true }, [Validators.required]],
    content: [null, [Validators.required]],
  });

  loading = signal(false);
  textContentId = signal<string | null>(null);

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    clipboard: { matchVisual: false },
  };

  quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'indent',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video',
  ];

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.textContentId.set(id);
      this.loadData(id);
    }
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.textContentService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as TextContent;

        this.formGroup.patchValue({
          id: model.id,
          slug: model.slug,
          description: model.description,
          content: model.content,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o texto',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.textContentId();
    if (!id) return;

    const content = this.normalizeContentLineBreaks(
      this.formGroup.get('content')?.value as string,
    );

    this.loading.set(true);

    let request$: Observable<ApiResult>;
    request$ = this.textContentService.update(id, { content });

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Texto atualizado com sucesso',
          life: 2000,
        });

        this.router.navigate(['/text-contents']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length === 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao atualizar o texto',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private normalizeContentLineBreaks(content: string): string {
    if (!content) return content;

    // Quill stores each line as a paragraph. Convert paragraphs into explicit
    // line breaks so rendered HTML keeps the expected spacing between lines.
    let normalized = content
      .trim()
      .replace(/<p>\s*<br\s*\/?\s*>\s*<\/p>/gi, '<br>')
      .replace(/<p>([\s\S]*?)<\/p>/gi, (_, inner: string) => `${inner}<br>`);

    normalized = normalized.replace(/(<br\s*\/?\s*>)+$/gi, '');

    return normalized;
  }
}
